import { INDIA_LOCALE } from '../config.ts';
import {
  isShut,
  type MarketCalendar,
  nextOpen,
  nextSessionDay,
  previousTradingDay,
  TRADED_VENUES,
  VENUE_LABEL,
  type Venue,
} from './calendar.ts';
import { whenSaid } from './calendar-said.ts';
import { andList } from './primitives.ts';
import { beijingAt, istAt, istClock, newYorkAt, todayInIst } from './when.ts';

export const IMPORTANCES = ['HIGH', 'MEDIUM', 'LOW'] as const;
export type Importance = (typeof IMPORTANCES)[number];

export const INDIA = 'India';

const UNITED_STATES = 'United States';

const CHINA = 'China';

/** The OPEC+ countries that meet to set their output. */
export const OPEC_PLUS = 'OPEC+';

export const OPEC_MEETING = 'Meeting';

/**
 * Zerodha's page for the world's releases; EIA's own schedules for the two that move MCX energy;
 * BLS's and BEA's own for the US releases they publish, with their hours and any reschedule; and
 * MSCI's, the Fed's, RBI's and OPEC+'s own dates.
 */
export const EVENT_SOURCES = [
  'ZERODHA',
  'EIA',
  'BLS',
  'BEA',
  'MSCI',
  'FOMC',
  'RBI',
  'OPEC',
] as const;

export const DESK_SOURCES = [
  'MSCI',
  'FOMC',
  'RBI',
  'OPEC',
] as const satisfies readonly EventSource[];
export type DeskSource = (typeof DESK_SOURCES)[number];
export type EventSource = (typeof EVENT_SOURCES)[number];

/** Every event is shown this far ahead; past it, only the key ones. */
export const EVENT_WINDOW_DAYS = 30;

export interface EconomicEvent {
  day: string;
  economy: string;
  name: string;
  importance: Importance;
  /** A range for a rate set as one. */
  previous: string | null;
  /** The print once it is out, where the source gives it. */
  actual: string | null;
  unit: string;
  at: string | null;
}

export const EIA_CRUDE = 'EIA Crude Oil Inventories';

export const EIA_GAS = 'EIA Natural Gas Storage';

export const COMMODITY_RELEASES: Record<string, string> = {
  [EIA_CRUDE]: 'CRUDEOIL',
  [EIA_GAS]: 'NATURALGAS',
};

/** What stands for the whole of NSE in `movesOf`: whoever is in any NSE name, cash or F&O. */
export const NSE_WIDE = 'NSE';

const PRECIOUS = ['GOLD', 'SILVER'];

/** China buys most of the world's base metals and is the marginal buyer of crude. */
const CHINA_DEMAND = ['COPPER', 'ZINC', 'ALUMINIUM', 'LEAD', 'NICKEL', 'CRUDEOIL'];

/**
 * The releases and decisions that move what a reader can be in, by economy and name. The dollar
 * and US real rates price gold and silver, and the Fed crude too; China's factories and its
 * imports price base metals and crude; OPEC+'s output, crude; RBI, the Budget and India's CPI
 * move all of NSE. One
 * release a day each, so China's trade figures are its balance alone and its PMI the
 * manufacturing one.
 */
const MACRO_MOVES: Record<string, readonly string[]> = {
  'United States|Central Bank Policy Rate': [...PRECIOUS, 'CRUDEOIL'],
  'United States|Inflation': PRECIOUS,
  'United States|Monthly Non Farm': PRECIOUS,
  'China|Manufacturing PMI': CHINA_DEMAND,
  'China|Merchandise Trade Balance': CHINA_DEMAND,
  [`${INDIA}|RBI Policy Rate`]: [NSE_WIDE],
  [`${INDIA}|Union Budget`]: [NSE_WIDE],
  [`${INDIA}|CPI Inflation`]: [NSE_WIDE],
  [`${OPEC_PLUS}|${OPEC_MEETING}`]: ['CRUDEOIL'],
};

/**
 * What an event moves that a reader can be in: a commodity, or `NSE_WIDE`. A commodity's own
 * release moves that commodity; an index rebalance moves the whole of NSE, since its funds trade
 * the index's every name at once and the index futures with them; the rest are `MACRO_MOVES`.
 */
export function movesOf(event: EconomicEvent): readonly string[] {
  const commodity = COMMODITY_RELEASES[event.name];
  if (commodity !== undefined) return [commodity];
  if (isRebalance(event)) return [NSE_WIDE];
  return MACRO_MOVES[`${event.economy}|${event.name}`] ?? [];
}

/** What a reader is in, as `movesOf` names it: their commodities, and NSE if any NSE name. */
export const reachOf = (commodities: Iterable<string>, nse: ReadonlySet<string>): Set<string> =>
  new Set([...commodities, ...(nse.size > 0 ? [NSE_WIDE] : [])]);

/** The banks whose rate moves the dollar, and so gold, crude and what foreigners do in Nifty. */
const CENTRAL_BANKS: Record<string, string> = {
  'United States': 'Fed',
  'Euro area': 'ECB',
  Japan: 'BoJ',
  'United Kingdom': 'BoE',
  China: 'PBoC',
};

const SHORT_NAME: Record<string, string> = { 'United States': 'US', 'United Kingdom': 'UK' };

const isPolicyRate = (event: Pick<EconomicEvent, 'name'>): boolean =>
  event.name.toLowerCase() === 'central bank policy rate';

/** `MSCI Index Review - Effective Date`: the first day at the new weights. */
const REVIEW_EFFECTIVE = /^(.+) Index Review - Effective Date$/;

const REVIEW_ANNOUNCED = /^(.+) Index Review - Announcement Date$/;

const REBALANCE = /^(.+) rebalance$/;

/** The index an Indian event of this pattern is about. */
const indexNamed = (
  event: Pick<EconomicEvent, 'economy' | 'name'>,
  pattern: RegExp,
): string | undefined => (event.economy === INDIA ? pattern.exec(event.name)?.[1] : undefined);

/** The flow itself, named by `tradedOn`: what moves prices is the day before the effective date. */
const isRebalance = (event: Pick<EconomicEvent, 'economy' | 'name'>): boolean =>
  indexNamed(event, REBALANCE) !== undefined;

/** An index's changes told: what will trade is known, and nothing trades on it yet. */
export const isAnnounced = (event: Pick<EconomicEvent, 'economy' | 'name'>): boolean =>
  indexNamed(event, REVIEW_ANNOUNCED) !== undefined;

/**
 * Whether an event can reach a reader at all, so whether it is worth keeping: India's, the US's and
 * China's, and the rate decisions the dollar answers to. The rest of the world's releases are never
 * shown.
 */
export const reachesReader = (event: Pick<EconomicEvent, 'economy' | 'name'>): boolean =>
  event.economy === INDIA ||
  event.economy === UNITED_STATES ||
  event.economy === CHINA ||
  `${event.economy}|${event.name}` in MACRO_MOVES ||
  (isPolicyRate(event) && event.economy in CENTRAL_BANKS);

/** Its hour has come, at most `ms` ago. */
export const isPastWithin = (event: Pick<EconomicEvent, 'at'>, now: Date, ms: number): boolean => {
  const since = event.at === null ? -1 : now.getTime() - Date.parse(event.at);
  return since >= 0 && since <= ms;
};

export const isKey = (event: EconomicEvent): boolean =>
  event.importance === 'HIGH' &&
  !isAnnounced(event) &&
  (event.economy === INDIA ||
    event.economy === 'United States' ||
    (isPolicyRate(event) && event.economy in CENTRAL_BANKS));

export function eventTitle(event: Pick<EconomicEvent, 'economy' | 'name'>, country = true): string {
  const announced = indexNamed(event, REVIEW_ANNOUNCED);
  if (announced !== undefined) return `${announced} index review: changes announced`;
  if (event.economy === INDIA) return event.name;
  const bank = CENTRAL_BANKS[event.economy];
  if (bank && isPolicyRate(event)) return `${bank} Policy Rate`;
  return country ? `${SHORT_NAME[event.economy] ?? event.economy} ${event.name}` : event.name;
}

export const eventSaid = (event: EconomicEvent): string =>
  event.at === null ? eventTitle(event) : `${eventTitle(event)} at ${istClock(event.at)}`;

interface ReleaseHour {
  economy: string;
  names: RegExp;
  at: (day: string) => Date;
}

const newYork =
  (hours: number, minutes = 0) =>
  (day: string) =>
    newYorkAt(day, hours * 60 + minutes);

/**
 * When a release lands, by its publisher's own standing rule, for the rows a feed dates but does not
 * time. Each rule names where it was verified (30 September 2026). An hour across midnight IST moves
 * the event to the IST day it lands on.
 */
const RELEASE_HOURS: readonly ReleaseHour[] = [
  // BLS: the CPI, the Employment Situation (payrolls and the unemployment rate) and the PPI, all
  // at 08:30 Eastern: https://www.bls.gov/schedule/news_release/empsit.htm and BLS's own release
  // calendar, https://www.bls.gov/schedule/news_release/bls.ics.
  {
    economy: UNITED_STATES,
    names: /^(Inflation|Monthly Non Farm|Unemployment Rate|Producer Price Index \(PPI\))$/,
    at: newYork(8, 30),
  },
  // BEA: GDP and Personal Income and Outlays (PCE), at 08:30 Eastern in BEA's own release dates,
  // https://apps.bea.gov/API/signup/release_dates.json.
  {
    economy: UNITED_STATES,
    names: /^(Real GDP( \(.+\))?|Personal Consumption Expenditure \(PCE\))$/,
    at: newYork(8, 30),
  },
  // The FOMC statement, "For release at 2:00 p.m.":
  // https://www.federalreserve.gov/newsevents/pressreleases/monetary20260729a.htm.
  { economy: UNITED_STATES, names: /^Central Bank Policy Rate$/, at: newYork(14) },
  // MOSPI's CPI, IIP and quarterly GDP are embargoed "till 4:00 PM": the June 2026 CPI, May 2026
  // IIP and Q4 2025-26 GDP press releases at https://www.mospi.gov.in/.
  {
    economy: INDIA,
    names: /^(CPI Inflation|Industrial Production|Real GDP)$/,
    at: (day) => istAt(day, '16:00'),
  },
  // The Governor announces the MPC's decision at 10:00 IST on its last day:
  // https://www.newsonair.gov.in/rbi-to-announce-monetary-policy-today-governor-sanjay-malhotra-to-speak-at-10-am.
  { economy: INDIA, names: /^RBI Policy Rate$/, at: (day) => istAt(day, '10:00') },
  // NBS publishes its PMIs at 9:30 Beijing time: the Regular Press Release Calendar of NBS in 2026,
  // https://www.stats.gov.cn/english/PressRelease/ReleaseCalendar/202512/t20251226_1962154.html.
  {
    economy: CHINA,
    names: /^(Manufacturing PMI|Non-Manufacturing Business Index|Comprehensive PMI output index)$/,
    at: (day) => beijingAt(day, '09:30'),
  },
];

/** A row a feed dated but did not time, timed by its publisher's rule; one with no rule as it was. */
export function timedByRule(event: EconomicEvent): EconomicEvent {
  if (event.at !== null) return event;
  const rule = RELEASE_HOURS.find(
    (one) => one.economy === event.economy && one.names.test(event.name),
  );
  if (rule === undefined) return event;
  const at = rule.at(event.day);
  return { ...event, day: todayInIst(at), at: at.toISOString() };
}

/** Which of the venues a reader trades are open at an instant. */
const openAt = (calendar: MarketCalendar, at: string): Venue[] =>
  TRADED_VENUES.filter((venue) => !isShut(calendar, venue, new Date(at)));

/** An event timed, with the venues open when it lands: whether a stop can act on it at once. */
export type LandedEvent = EconomicEvent & { open?: readonly Venue[] | null };

export const landed = <E extends EconomicEvent>(
  calendar: MarketCalendar,
  events: readonly E[],
): (E & LandedEvent)[] =>
  events.map((event) => ({
    ...event,
    open: event.at === null ? null : openAt(calendar, event.at),
  }));

/** `NSE F&O and MCX open`, `MCX open, NSE F&O shut`. */
export function openSaid(open: readonly Venue[]): string {
  const shut = TRADED_VENUES.filter((venue) => !open.includes(venue));
  const names = (venues: readonly Venue[]) => venues.map((one) => VENUE_LABEL[one]).join(' and ');
  if (shut.length === 0) return `${names(open)} open`;
  if (open.length === 0) return `${names(shut)} shut`;
  return `${names(open)} open, ${names(shut)} shut`;
}

/** The event's name and hour, and which venues are open then: `US Inflation at 18:00 (MCX open, NSE F&O shut)`. */
export const eventLandsSaid = (event: LandedEvent): string =>
  event.open == null ? eventSaid(event) : `${eventSaid(event)} (${openSaid(event.open)})`;

const grouped = new Intl.NumberFormat(INDIA_LOCALE);

const currency = (symbol: string, figure: string): string =>
  figure.startsWith('-') ? `-${symbol}${figure.slice(1)}` : `${symbol}${figure}`;

const UNIT_SAID: Record<string, (figure: string) => string> = {
  '%': (figure) => `${figure}%`,
  'YoY%': (figure) => `${figure}% YoY`,
  'US$ bn': (figure) => `${currency('US$', figure)} bn`,
  'Rs bn': (figure) => `${currency('₹', figure)} bn`,
  Mn: (figure) => `${figure} mn`,
  Number: (figure) => grouped.format(Number(figure)),
};

const figureSaid = (figure: string | null, unit: string): string | null =>
  figure === null ? null : (UNIT_SAID[unit]?.(figure) ?? `${figure} ${unit}`);

export const previousSaid = ({ previous, unit }: EconomicEvent): string | null =>
  figureSaid(previous, unit);

export const actualSaid = ({ actual, unit }: EconomicEvent): string | null =>
  figureSaid(actual, unit);

/**
 * An index review's effective date is its first day at the new weights, so the funds that track
 * the index trade at NSE's close the session before: that is the event said. It is dated to NSE's
 * closing auction where the session has one, else to its close. The announcement stays on its own
 * day, not key. A flow already past, its effective date today, is dropped with the rest of the
 * past.
 */
export function tradedOn(
  calendar: MarketCalendar,
  events: readonly EconomicEvent[],
  today: string,
): EconomicEvent[] {
  const traded = events.map((event) => {
    const index = indexNamed(event, REVIEW_EFFECTIVE);
    if (index === undefined) return event;
    const day = previousTradingDay(calendar, 'NSE', event.day);
    const session = day === null ? null : calendar.session('NSE', day);
    if (day === null || session === null) return event;
    const auction = session.phases.find((one) => one.phase === 'CLOSING_AUCTION');
    return {
      ...event,
      day,
      name: `${index} rebalance`,
      previous: null,
      actual: null,
      at: (auction?.from ?? session.close).toISOString(),
    };
  });
  return traded.filter((one) => one.day >= today).sort((a, b) => a.day.localeCompare(b.day));
}

/** What the flow is, beside its name: said wherever the event is. */
export const eventNote = (event: Pick<EconomicEvent, 'economy' | 'name'>): string | null =>
  isRebalance(event) ? 'index funds trade at the NSE close' : null;

const byDay = (a: EconomicEvent, b: EconomicEvent): number =>
  a.day.localeCompare(b.day) || Number(b.economy === INDIA) - Number(a.economy === INDIA);

/**
 * Up to the next day a market trades, so a weekend's are heard on the Friday: the key ones, and
 * those that move what the reader is in (`reach`, `reachOf`).
 */
export function keyEventsAhead<E extends EconomicEvent>(
  events: readonly E[],
  calendar: MarketCalendar,
  today: string,
  reach: ReadonlySet<string> = new Set(),
): E[] {
  const until = nextSessionDay(calendar, today);
  const key = (one: EconomicEvent): boolean =>
    isKey(one) || movesOf(one).some((moved) => reach.has(moved));
  return events.filter((one) => key(one) && one.day >= today && one.day <= until).sort(byDay);
}

export function aheadSaid(events: readonly EconomicEvent[], today: string): string[] {
  const days = new Map<string, string[]>();
  for (const event of events)
    days.set(event.day, [...(days.get(event.day) ?? []), eventTitle(event)]);
  return [...days].map(([day, names]) => `${andList(names)} ${whenSaid(day, today)}`);
}

/** What a reader holds, as `heldInto` reads it: the name, the venue it trades on, and what moves it (`movesOf`). */
export interface Held {
  underlying: string;
  venue: Venue;
  moves: string;
}

/** A recommendation's name as `heldInto` reads it: a commodity by itself, anything else as NSE. */
export const heldOf = ({ underlying, market }: { underlying: string; market: string }): Held => ({
  underlying,
  venue: market === 'COMMODITY' ? 'MCX' : 'NFO',
  moves: market === 'COMMODITY' ? underlying : NSE_WIDE,
});

/** When an event lands: its hour, or for one without, the start of its IST day. */
const landsAt = (event: EconomicEvent): string =>
  event.at ?? istAt(event.day, '00:00').toISOString();

/** A key event landing on a held name while its venue is shut. */
export interface HeldInto {
  held: Held;
  event: EconomicEvent;
  /** When the venue next opens, and a stop can act again; null past the calendar. */
  opens: string | null;
}

/**
 * Held into the event: the first key event up to the next session (`keyEventsAhead`) that moves a
 * held name and lands while its venue is shut, so a stop cannot act on it until the open, and the
 * open can gap past the stop. One with no hour lands while it is shut only on a day the venue does
 * not trade at all, such as OPEC+ meeting on a Sunday.
 */
export function heldInto(
  events: readonly EconomicEvent[],
  calendar: MarketCalendar,
  today: string,
  held: readonly Held[],
): HeldInto[] {
  return held.flatMap((one) => {
    const event = keyEventsAhead(events, calendar, today, new Set([one.moves]))
      .filter(
        (each) =>
          movesOf(each).includes(one.moves) &&
          (each.at === null
            ? calendar.session(one.venue, each.day) === null
            : isShut(calendar, one.venue, new Date(each.at))),
      )
      .sort((a, b) => landsAt(a).localeCompare(landsAt(b)))[0];
    if (event === undefined) return [];
    const opens = nextOpen(calendar, one.venue, new Date(landsAt(event)));
    return [{ held: one, event, opens: opens?.open.toISOString() ?? null }];
  });
}
